import { Dialog } from "../../ui/layers";
import { formatActiveDateTime } from "../../i18n/formatting";
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Clock3,
  KeyRound,
  RefreshCcw,
  Settings2,
  ShieldCheck,
  ShieldX,
} from "lucide-react";

import {
  bootstrapWindowsBetaTuf,
  getTufMaintenancePolicy,
  renewWindowsBetaTufMetadata,
} from "./keysTrustApi";
import { TufMaintenancePolicyEditor } from "./TufMaintenancePolicyEditor";
import { useKeysTrustManagedCopy } from "./keysTrustManagedCatalog";
import { readTufHealth, type TufHealth } from "./tufHealthApi";
import "../../styles/keys-trust-managed.css";
import "../../styles/keys-trust-tuf.css";

function remaining(seconds: number): string {
  const hours = Math.max(0, Math.floor(seconds / 3600));
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function TufHealthPanel({
  onSessionUnavailable,
}: {
  onSessionUnavailable?: () => void;
}) {
  const { t } = useKeysTrustManagedCopy();
  const [health, setHealth] = useState<TufHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setHealth(await readTufHealth());
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("tufHealthLoadError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [t]);

  async function bootstrap() {
    setBootstrapping(true);
    setError(null);
    setMessage(null);
    try {
      const result = await bootstrapWindowsBetaTuf();
      setMessage(
        result.mode === "already_complete"
          ? t("tufBootstrapAlready", { version: result.root_version })
          : result.mode === "publication_recovered"
            ? t("tufBootstrapRecovered", { version: result.root_version })
            : t("tufBootstrapInitialized", { version: result.root_version }),
      );
      setHealth(await readTufHealth());
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("tufBootstrapError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBootstrapping(false);
    }
  }

  async function renew() {
    setRenewing(true);
    setError(null);
    setMessage(null);
    try {
      const policy = await getTufMaintenancePolicy();
      if (!policy) {
        setPolicyOpen(true);
        setMessage(t("tufPolicyRequiredBeforeRenewal"));
        return;
      }
      const result = await renewWindowsBetaTufMetadata();
      setMessage(
        result.status === "renewed"
          ? t("tufMaintenanceRenewed", { version: result.active_metadata_version })
          : t("tufMaintenanceReconciled", { version: result.active_metadata_version }),
      );
      setHealth(await readTufHealth());
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("tufMaintenanceError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setRenewing(false);
    }
  }

  const blocked = health?.release_blocking ?? false;
  const rootRole = health?.roles.find((role) => role.role === "root");
  const mutableRenewalRoles = health?.roles.filter(
    (role) => role.role !== "root" && (role.status === "expired" || role.status === "expiring"),
  ) ?? [];
  const renewalVisible = mutableRenewalRoles.length > 0 && rootRole?.status !== "expired";
  const icon = blocked
    ? <ShieldX size={18} />
    : health?.status === "expiring"
      ? <AlertTriangle size={18} />
      : <ShieldCheck size={18} />;
  const bootstrapVisible = health?.status === "unconfigured" || health?.status === "unavailable";

  return (
    <section className="keys-trust-tuf" aria-labelledby="keys-trust-tuf-title">
      <header>
        <div>
          <p className="admin-v12-eyebrow">{t("tufPublicUpdateTrustEyebrow")}</p>
          <h2 id="keys-trust-tuf-title">{t("tufHealth")}</h2>
          <p>{t("tufHealthBody")}</p>
        </div>
        <button
          type="button"
          className="admin-v12-secondary"
          onClick={() => void load()}
          disabled={loading || bootstrapping || renewing}
        >
          <RefreshCcw size={15} />
          {loading ? t("loading") : t("refresh")}
        </button>
      </header>

      {error && (
        <p className="admin-v12-form-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="keys-trust-managed-success" role="status">
          {message}
        </p>
      )}

      {health && (
        <div
          className={`keys-trust-tuf-state state-${health.status}`}
          aria-live="polite"
        >
          <span>
            {icon}
            <strong>{health.status === "healthy" ? t("tufStatusHealthy") : health.status === "expiring" ? t("tufStatusExpiring") : health.status === "expired" ? t("tufStatusExpired") : health.status === "invalid" ? t("tufStatusInvalid") : health.status === "unavailable" ? t("tufStatusUnavailable") : t("tufStatusUnconfigured")}</strong>
          </span>
          <b>{blocked ? t("tufReleaseBlocking") : t("tufReadOnlyHealth")}</b>
          {health.detail && <p>{health.detail}</p>}
        </div>
      )}

      {health && (
        <div className="tuf-policy-entry">
          <button
            type="button"
            className="admin-v12-secondary"
            onClick={() => setPolicyOpen((value) => !value)}
          >
            <Settings2 size={15} />
            {t("tufPolicyOpen")}
          </button>
          <span>{t("tufPolicyEntryHelp")}</span>
        </div>
      )}

      <Dialog open={policyOpen} onDismiss={()=>setPolicyOpen(false)} title={t("tufPolicyTitle")}>
        <TufMaintenancePolicyEditor startEditing
          {...(onSessionUnavailable ? { onSessionUnavailable } : {})}
          onSaved={() => {
            setPolicyOpen(false);
            setMessage(t("tufPolicySavedContinue"));
          }}
        />
      </Dialog>

      {renewalVisible && (
        <div className="keys-trust-managed-action" aria-label={t("tufMaintenanceAria")}>
          <div>
            <strong><RefreshCcw size={15} /> {t("tufMaintenanceTitle")}</strong>
            <p>{t("tufMaintenanceBody")}</p>
          </div>
          <button
            type="button"
            className="admin-v12-primary"
            onClick={() => void renew()}
            disabled={loading || bootstrapping || renewing}
          >
            <RefreshCcw size={15} />
            {renewing ? t("tufMaintenanceWorking") : t("tufMaintenanceAction")}
          </button>
          <p>{t("tufMaintenanceBoundary")}</p>
        </div>
      )}

      {bootstrapVisible && (
        <div className="keys-trust-managed-action" aria-label={t("tufBootstrapAria")}>
          <div>
            <strong><KeyRound size={15} /> {t("tufBootstrapTitle")}</strong>
            <p>{t("tufBootstrapBody")}</p>
          </div>
          <button
            type="button"
            className="admin-v12-primary"
            onClick={() => void bootstrap()}
            disabled={loading || bootstrapping || renewing}
          >
            <ShieldCheck size={15} />
            {bootstrapping ? t("initializing") : t("tufBootstrapAction")}
          </button>
          <p>{t("tufRecentMfaBoundary")}</p>
        </div>
      )}

      <div className="keys-trust-tuf-roles">
        {health?.roles.map((role) => (
          <article key={role.role}>
            <header>
              <strong>{role.role}</strong>
              <span className={`state-${role.status}`}>{role.status}</span>
            </header>
            <dl>
              <div>
                <dt>{t("version")}</dt>
                <dd>{role.version}</dd>
              </div>
              <div>
                <dt>{t("remaining")}</dt>
                <dd>
                  <Clock3 size={13} />
                  {remaining(role.remaining_seconds)}
                </dd>
              </div>
              <div>
                <dt>{t("expires")}</dt>
                <dd>{formatActiveDateTime(role.expires_at, { dateStyle: "medium", timeStyle: "short" })}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>

      <footer>{t("tufHealthFooter")}</footer>
    </section>
  );
}
